import { useEffect } from "react";
import useContent from "@/hooks/useContent";

/**
 * useSeo(pageKey, defaults)
 *   defaults = { title, description }
 *
 * Reads `seo_title` and `seo_description` from the page's CMS content
 * (falling back to defaults). Applies to <title> and <meta name="description">.
 * Restores the previous values on unmount so navigating between pages stays clean.
 */
export default function useSeo(pageKey, { title, description } = {}) {
  const t = useContent(pageKey);
  const nextTitle = t("seo_title", title);
  const nextDesc = t("seo_description", description);

  useEffect(() => {
    if (!nextTitle && !nextDesc) return;
    const prevTitle = document.title;
    let descTag = document.querySelector('meta[name="description"]');
    const prevDesc = descTag ? descTag.getAttribute("content") : null;
    const createdTag = !descTag;

    if (nextTitle) document.title = nextTitle;
    if (nextDesc) {
      if (!descTag) {
        descTag = document.createElement("meta");
        descTag.setAttribute("name", "description");
        document.head.appendChild(descTag);
      }
      descTag.setAttribute("content", nextDesc);
    }

    return () => {
      document.title = prevTitle;
      if (createdTag && descTag) {
        descTag.parentNode && descTag.parentNode.removeChild(descTag);
      } else if (descTag && prevDesc != null) {
        descTag.setAttribute("content", prevDesc);
      }
    };
  }, [nextTitle, nextDesc]);
}
